import http from './http'
import { getMediaOrigin } from './config'

const LIST_PAGE_PARAMS = { page: 1, per_page: 999 }
let homeSolutionsCache = null
let homeSolutionsPromise = null
const solutionDetailCache = new Map()
const solutionDetailPromise = new Map()

function unwrapArray(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  const d = payload.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data
  return []
}

function resolveStorageUrl(path) {
  if (!path || typeof path !== 'string') return ''
  const t = path.trim()
  if (!t) return ''
  // 兼容：后端可能直接返回绝对地址（包含 /storage/uploads/...），前端期望显示为 /uploads/...
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:')) {
    if (/\/storage\/uploads/i.test(t)) return t.replace(/\/storage\/uploads/ig, '/uploads')
    return t
  }
  const base = getMediaOrigin().replace(/\/$/, '')
  // 兼容：后端有时会把上传目录写成 storage/uploads/...，前端期望去掉 storage/
  if (t.startsWith('/storage/uploads') || t.startsWith('storage/uploads')) {
    const rest = t.replace(/^\/?storage\//, '') // uploads/...
    return `${base}/${rest.replace(/^\//, '')}`
  }
  if (t.startsWith('/storage/') || t.startsWith('storage/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  if (t.startsWith('/uploads/') || t.startsWith('uploads/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  return `${base}/storage/${t.replace(/^\//, '')}`
}

function resolveHtmlUrl(path) {
  if (!path || typeof path !== 'string') return path
  const t = path.trim()
  if (!t) return t
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:') || t.startsWith('#')) {
    if (/\/storage\/uploads/i.test(t)) return t.replace(/\/storage\/uploads/ig, '/uploads')
    return t
  }
  const base = getMediaOrigin().replace(/\/$/, '')
  if (t.startsWith('/storage/uploads') || t.startsWith('storage/uploads')) {
    const rest = t.replace(/^\/?storage\//, '') // uploads/...
    return `${base}/${rest.replace(/^\//, '')}`
  }
  return `${base}/${t.replace(/^\//, '')}`
}

function normalizeRichHtml(raw) {
  if (typeof raw !== 'string') return ''
  const html = raw.trim()
  if (!html) return ''
  return html
    .replace(/(src|href|data-href)=["']([^"']+)["']/gi, (_m, attr, url) => {
      const resolved = resolveHtmlUrl(url)
      return `${attr}="${resolved}"`
    })
}

function normalizeSolution(row) {
  if (!row || typeof row !== 'object') return null
  const cover =
    row.cover_photo_path ||
    row.cover ||
    row.image ||
    row.pic ||
    row.image_url ||
    row.imageUrl ||
    ''
  return {
    id: row.id ?? row.solution_id,
    title: row.title || row.name || row.solution_name || '解决方案',
    subtitle: row.subtitle || row.description || row.summary || '',
    image: resolveStorageUrl(typeof cover === 'string' ? cover : ''),
    link: row.link || row.url || row.href || ''
  }
}

function normalizeSolutionDetail(row) {
  const base = normalizeSolution(row)
  if (!base) return null
  const intro =
    (typeof row.intro === 'string' && row.intro) ||
    (typeof row.summary === 'string' && row.summary) ||
    (typeof base.subtitle === 'string' ? base.subtitle : '') ||
    ''
  const raw =
    row.body ||
    row.content_html ||
    row.html_content ||
    row.content ||
    row.detail ||
    ''
  const s = typeof raw === 'string' ? raw.trim() : ''
  const isHtml = s.length > 0 && /<[a-z][\s\S]*>/i.test(s)
  return {
    ...base,
    intro,
    contentHtml: isHtml ? normalizeRichHtml(s) : '',
    contentPlain: !isHtml && s ? s : ''
  }
}

export const FALLBACK_SOLUTIONS = [
  {
    id: 1,
    title: 'AI视觉空间感知整体解决方案',
    subtitle: '以视觉识别与空间行为分析优化馆内运营体验。',
    image: '',
    intro: '基于视觉感知、客流分析与路径热力图，帮助图书馆进行空间利用优化。',
    contentPlain: ''
  },
  {
    id: 2,
    title: '图书馆人工智能解决方案',
    subtitle: '围绕咨询、推荐、盘点、导览构建智能服务闭环。',
    image: '',
    intro: '融合大模型问答、知识库检索、智能导览与业务协同。',
    contentPlain: ''
  },
  {
    id: 3,
    title: '智能立体书库',
    subtitle: '高密度存储、自动调阅与分拣，释放馆舍空间。',
    image: '',
    intro: '立体库与分拣系统联动，缩短图书调取时间。',
    contentPlain: ''
  },
  {
    id: 4,
    title: '公共图书馆解决方案',
    subtitle: '覆盖借还、门禁、盘点、运营分析等公共服务场景。',
    image: '',
    intro: '适配城市级图书馆高客流和多分馆协同需求。',
    contentPlain: ''
  },
  {
    id: 5,
    title: '校园图书馆解决方案',
    subtitle: '联动校园身份与教务体系，支撑教学科研阅读。',
    image: '',
    intro: '面向高校与中职院校的馆藏管理与阅读服务升级。',
    contentPlain: ''
  },
  {
    id: 6,
    title: '城市书房解决方案',
    subtitle: '轻量部署与远程运维，延展城市阅读服务触点。',
    image: '',
    intro: '构建“15分钟阅读圈”的标准化智能书房方案。',
    contentPlain: ''
  }
]

export async function fetchSolutions() {
  if (Array.isArray(homeSolutionsCache)) return homeSolutionsCache.map((s) => ({ ...s }))
  if (homeSolutionsPromise) return homeSolutionsPromise.then((list) => list.map((s) => ({ ...s })))

  homeSolutionsPromise = (async () => {
    try {
      const { data } = await http.get('/api/v1/solutions', { params: LIST_PAGE_PARAMS })
      const raw = data?.data !== undefined ? data.data : data
      const list = unwrapArray({ data: raw }).map(normalizeSolution).filter(Boolean)
      if (list.length) return list
    } catch (e) {
      console.warn('[solutions] 列表接口失败，使用本地数据', e)
    }
    return FALLBACK_SOLUTIONS.map((s) => ({ ...s }))
  })()
    .then((list) => {
      homeSolutionsCache = list
      return list
    })
    .finally(() => {
      homeSolutionsPromise = null
    })

  return homeSolutionsPromise.then((list) => list.map((s) => ({ ...s })))
}

export async function fetchSolutionDetail(solutionId) {
  const id = solutionId
  if (id == null || id === '') return null
  const key = String(id)
  if (solutionDetailCache.has(key)) {
    const cached = solutionDetailCache.get(key)
    return cached ? { ...cached } : null
  }
  if (solutionDetailPromise.has(key)) {
    return solutionDetailPromise.get(key).then((d) => (d ? { ...d } : null))
  }

  const tryPaths = [`/api/v1/solutions/${id}`, `/api/v1/solution/${id}`]
  const req = (async () => {
    for (const url of tryPaths) {
      try {
        const { data } = await http.get(url)
        const raw = data?.data !== undefined ? data.data : data
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          const d = normalizeSolutionDetail(raw)
          if (d) return d
        }
      } catch (e) {
        if (url === tryPaths[tryPaths.length - 1]) {
          console.warn('[solutions] 详情接口失败', id, e)
        }
      }
    }

    const local = FALLBACK_SOLUTIONS.find((s) => String(s.id) === String(id))
    return local ? { ...local } : null
  })()
    .then((detail) => {
      solutionDetailCache.set(key, detail)
      return detail
    })
    .finally(() => {
      solutionDetailPromise.delete(key)
    })

  solutionDetailPromise.set(key, req)
  return req.then((d) => (d ? { ...d } : null))
}
